import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  StatusPill,
  Pipeline,
  STATUSES,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components';
import { api, useApi, useMutation } from '../../api';
import type { OrderItemView, AuditEntryView } from '../../api';
import { useAuth, useMoneyVisible } from '../../auth';
import {
  OrderStatus,
  PaymentMethod,
  AuditActorType,
  AuditTone,
  DocumentType,
  TransitionEffect,
  findTransition,
  can,
} from '@jalupro/shared';
import type { OverrideOrderStatusDto, AddressDto } from '@jalupro/shared';
import { useT } from '../../i18n';
import dict from './OrderDetail.i18n';

/**
 * Admin · Order detail (2-pane) — route /admin/orders/:id.
 * Ported pixel-faithfully from Screen7B_OrderDetailClean
 * (design-source/screen-7-admin-order-detail.jsx).
 *
 * The layout (PortalLayout/AdminLayout) supplies <CleanSidebar>; this screen renders
 * only the content to the right of it: a topbar + a left scroll column (pipeline +
 * on-hold banner + customer/items + activity log) + a 340px right rail (quick actions
 * + manual status override).
 *
 * Wired to:
 *   GET  /orders/:id          → order detail incl. items, client, documents, auditLog
 *   POST /orders/:id/override → admin/super-admin manual status override (reason recorded)
 *
 * Phase-1 scope (DECISIONS.md): payments are PREPAID-only and ON_HOLD_ADMIN_REVIEW is
 * never auto-triggered. The source's SEPA-specific quick actions ("Resend SEPA mandate
 * form", "Mark mandate received", "Convert payment → IBAN") and the WhatsApp tracking
 * line are dropped; the on-hold banner only renders if the order actually carries that
 * status (reachable via a future override). The "Setting to X will trigger" warning is
 * derived from the order state-machine's declared transition effects (re-confirm
 * semantics from the API), not hardcoded. Confirm-override is gated to ADMIN/SUPER_ADMIN.
 */


/** Euro from integer cents, Spanish formatting (e.g. 231000 → "€2.310,00"). */
function euros(cents: number): string {
  return `€${(cents / 100).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Human label for a payment method (Phase 1: Stripe / PayPal / Bank transfer). */
const PAYMENT_LABEL: Record<PaymentMethod, string> = {
  [PaymentMethod.STRIPE]: 'Card (Stripe)',
  [PaymentMethod.PAYPAL]: 'PayPal',
  [PaymentMethod.IBAN]: 'Bank transfer (IBAN)',
};
function paymentLabel(m: PaymentMethod): string {
  return PAYMENT_LABEL[m] ?? m;
}

/** Single-line billing/shipping address from the client's structured address. */
function addressLine(addr: AddressDto | null | undefined): string {
  if (!addr) return '—';
  return [addr.line1, addr.line2, addr.postalCode, addr.city, addr.province, addr.countryCode]
    .filter(Boolean)
    .join(', ');
}

/**
 * Audit tag (auto / staff) derived from the entry's actor. The audit entry carries no
 * role, so any USER action is tagged "staff"; SYSTEM/WEBHOOK/JOB are automations ("auto").
 */
function auditTag(e: AuditEntryView): { tag: string; cls: string } {
  if (e.actorType === AuditActorType.USER) {
    return { tag: 'staff', cls: 'amber' };
  }
  return { tag: 'auto', cls: 'blue' };
}

/** Audit timestamp like "18 May · 14:22". */
function auditTime(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

/**
 * Maps a TransitionEffect to the i18n dict key used to render its human-readable copy.
 * Translated strings live in OrderDetail.i18n.ts (effect_* keys).
 */
const EFFECT_KEY: Partial<Record<TransitionEffect, string>> = {
  [TransitionEffect.HOLDED_CREATE_PROFORMA]: 'effect_HOLDED_CREATE_PROFORMA',
  [TransitionEffect.HOLDED_SEND_PROFORMA]: 'effect_HOLDED_SEND_PROFORMA',
  [TransitionEffect.HOLDED_CREATE_INVOICE]: 'effect_HOLDED_CREATE_INVOICE',
  [TransitionEffect.HOLDED_SEND_INVOICE]: 'effect_HOLDED_SEND_INVOICE',
  [TransitionEffect.HOLDED_CREATE_WAYBILL]: 'effect_HOLDED_CREATE_WAYBILL',
  [TransitionEffect.SENDCLOUD_CREATE_LABEL]: 'effect_SENDCLOUD_CREATE_LABEL',
  [TransitionEffect.NOTIFY_TRACKING_READY]: 'effect_NOTIFY_TRACKING_READY',
  [TransitionEffect.SCHEDULE_TRACKING_POLL]: 'effect_SCHEDULE_TRACKING_POLL',
  [TransitionEffect.NOTIFY_DELIVERED]: 'effect_NOTIFY_DELIVERED',
  [TransitionEffect.NOTIFY_DELIVERY_PROBLEM]: 'effect_NOTIFY_DELIVERY_PROBLEM',
};

/**
 * The automation lines an override into `target` will re-confirm, given the order's
 * current status. Returns the i18n dict keys for the matching transition's effects
 * (excluding the audit-reason bookkeeping effect, which is implicit for every override).
 */
function overrideEffectKeys(from: OrderStatus, target: OrderStatus): string[] {
  const t = findTransition(from, target);
  if (!t) return [];
  return t.effects
    .filter((e) => e !== TransitionEffect.RECORD_OVERRIDE_REASON)
    .map((e) => EFFECT_KEY[e])
    .filter((c): c is string => Boolean(c));
}

export default function AdminOrderDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const { role } = useAuth();
  const showMoney = useMoneyVisible();
  // Manual status change / archive: matrix orders.manualStatus (effective, per render).
  const canOverride = role != null && can(role, 'orders.manualStatus');
  const t = useT(dict);

  const { data, loading, error, reload } = useApi(() => api.orders.get(id), [id]);

  // Override panel local state.
  const [target, setTarget] = useState<OrderStatus | null>(null);
  const [reason, setReason] = useState('');
  const override = useMutation(
    (dto: OverrideOrderStatusDto) => api.orders.override(id, dto),
  );
  const archiveMutation = useMutation(api.orders.archive);
  const restoreMutation = useMutation(api.orders.restore);
  const navigate = useNavigate();

  const effectKeys = useMemo(() => {
    if (!data || !target) return [];
    return overrideEffectKeys(data.status, target);
  }, [data, target]);

  if (loading) return <Shell><LoadingState label={t('loadingOrder')} /></Shell>;
  if (error) return <Shell><ErrorState message={error.message} onRetry={reload} /></Shell>;
  if (!data) return <Shell><EmptyState title={t('orderNotFound')} /></Shell>;

  const order = data;
  const onHold = order.status === OrderStatus.ON_HOLD_ADMIN_REVIEW;
  const proforma = order.documents.find((d) => d.type === DocumentType.PROFORMA);
  // Override needs both a target (different from current) and a reason (min 3 chars).
  const reasonOk = reason.trim().length >= 3;
  const targetOk = target != null && target !== order.status;
  const canConfirm = canOverride && targetOk && reasonOk && !override.loading;

  function resetOverride() {
    setTarget(null);
    setReason('');
    override.reset();
  }

  async function confirmOverride() {
    if (!targetOk || !reasonOk || !target) return;
    try {
      await override.mutate({ status: target, reason: reason.trim() });
      resetOverride();
      reload();
    } catch {
      /* override.error is set + surfaced below */
    }
  }

  async function doArchive() {
    if (!window.confirm(t('archiveConfirm', { code: order.code }))) return;
    try {
      await archiveMutation.mutate(id);
      navigate('/admin/orders');
    } catch {
      /* archiveMutation.error is surfaced below */
    }
  }

  async function doRestore() {
    try {
      await restoreMutation.mutate(id);
      reload();
    } catch {
      /* restoreMutation.error is surfaced below */
    }
  }

  return (
    <div className="cl col grow m-scroll" style={{ background: 'var(--paper)', overflow: 'hidden', minWidth: 0 }}>
      {/* Topbar */}
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="col">
          <div className="meta">{t('breadcrumbOrders')}{onHold ? t('breadcrumbOnHold') : ''}</div>
          <div className="row gap-8 center">
            <span className="h1">{order.clientName}</span>
            <span className="meta">·</span>
            <span className="mono meta">{order.code}</span>
            <StatusPill status={order.status} />
            {order.archivedAt && <span className="pill amber">{t('archivedBadge')}</span>}
          </div>
        </div>
        <div className="row gap-8">
          {proforma?.pdfUrl ? (
            <a className="btn" href={proforma.pdfUrl} target="_blank" rel="noreferrer">
              {t('btnViewProformaPdf')}
            </a>
          ) : (
            <button type="button" className="btn" disabled>
              {t('btnViewProformaPdf')}
            </button>
          )}
          {canOverride && (
            <button
              type="button"
              className="btn danger"
              onClick={() => setTarget((prev) => prev ?? order.status)}
            >
              {t('btnManualOverride')}
            </button>
          )}
          {canOverride &&
            (order.archivedAt ? (
              <button
                type="button"
                className="btn"
                disabled={restoreMutation.loading}
                onClick={() => void doRestore()}
              >
                {restoreMutation.loading ? t('restoringBtn') : t('restoreBtn')}
              </button>
            ) : (
              <button
                type="button"
                className="btn red"
                disabled={archiveMutation.loading}
                onClick={() => void doArchive()}
              >
                {archiveMutation.loading ? t('archivingBtn') : t('archiveBtn')}
              </button>
            ))}
        </div>
      </div>

      <div className="row grow m-stack" style={{ overflow: 'hidden' }}>
        {/* Left scroll column */}
        <div className="col grow" style={{ overflow: 'auto', padding: '18px 22px', minWidth: 0 }}>
          {(archiveMutation.error || restoreMutation.error) && (
            <div className="mb-12">
              <ErrorState
                message={(archiveMutation.error ?? restoreMutation.error)!.message}
              />
            </div>
          )}
          {/* Pipeline panel */}
          <div className="panel" style={{ padding: '18px 20px' }}>
            <div className="row between center mb-12">
              <div className="h2">{t('pipelineHeading')}</div>
              <div className="meta">
                {t('pipelineCreated', {
                  date: new Date(order.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                  }),
                  agent: order.agentName,
                })}
              </div>
            </div>
            <Pipeline status={order.status} />
            {onHold && (
              <div
                className="row gap-12 mt-16"
                style={{
                  padding: '10px 12px',
                  background: 'var(--red-bg)',
                  borderRadius: 6,
                  border: '1px solid var(--red-line)',
                }}
              >
                <div style={{ color: 'var(--red)', fontWeight: 600 }}>⏸</div>
                <div className="col grow">
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--red)' }}>
                    {t('onHoldTitle')}
                  </div>
                  <div className="meta">{t('onHoldHint')}</div>
                </div>
              </div>
            )}
          </div>

          {/* Customer + items grid */}
          <div className="row gap-16 mt-16">
            <div className="panel grow" style={{ padding: 16 }}>
              <div className="h3 mb-8">{t('customerHeading')}</div>
              <div className="col gap-6">
                <Field k={t('fieldCompany')} v={order.client.legalName ?? order.client.company} />
                {order.client.tradeName && <Field k={t('fieldTradeName')} v={order.client.tradeName} />}
                <Field k={t('fieldCifVat')} v={order.client.cifVat} />
                <Field k={t('fieldEmail')} v={order.client.emails[0] ?? '—'} />
                <Field k={t('fieldPhone')} v={order.client.phone ?? '—'} />
                <Field k={t('fieldBilling')} v={addressLine(order.client.billingAddress)} />
                <Field
                  k={t('fieldShipping')}
                  v={
                    order.shippingSameAsBilling
                      ? addressLine(order.client.billingAddress)
                      : addressLine(
                          order.shippingAddress ??
                            order.client.shippingAddress ??
                            order.client.billingAddress,
                        )
                  }
                />
                <Field k={t('fieldPayment')} v={paymentLabel(order.paymentMethod)} />
              </div>
            </div>
            <div className="panel grow" style={{ padding: 16 }}>
              <div className="h3 mb-8">{t('itemsHeading')}</div>
              <table>
                <tbody>
                  {order.items.map((it: OrderItemView) => (
                    <tr key={it.id}>
                      <td style={{ fontSize: 14 }}>
                        {it.productName} ×{it.qty}
                        {it.foc && <span className="pill green" style={{ marginLeft: 6 }}>FOC</span>}
                      </td>
                      {showMoney && (
                        <td className="mono" style={{ textAlign: 'right', fontSize: 13 }}>
                          {it.foc ? '€0,00' : euros(it.unitPriceCents * it.qty)}
                        </td>
                      )}
                    </tr>
                  ))}
                  {order.marketingPack && (
                    <tr>
                      <td style={{ fontSize: 14 }}>{t('marketingPack')}</td>
                      {showMoney && <td className="mono" style={{ textAlign: 'right', fontSize: 13 }}>€0,00</td>}
                    </tr>
                  )}
                </tbody>
              </table>
              {showMoney && (
                <>
                  <div className="divider mt-8" />
                  <div className="row between mt-8">
                    <span style={{ fontWeight: 600 }}>{t('totalLabel')}</span>
                    <span className="mono" style={{ fontSize: 16, fontWeight: 600 }}>
                      {euros(order.totalCents)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Additional notes */}
          <div className="panel mt-16" style={{ padding: 16 }}>
            <div className="h3 mb-8">{t('notesHeading')}</div>
            {order.notes && order.notes.trim() ? (
              <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{order.notes}</div>
            ) : (
              <div className="meta">{t('notesEmpty')}</div>
            )}
          </div>

          {/* Activity log */}
          <div className="panel mt-16" style={{ padding: 16 }}>
            <div className="row between center mb-8">
              <div className="h3">{t('activityHeading')}</div>
              <div className="meta">{t('activitySubheading')}</div>
            </div>
            {order.auditLog.length === 0 ? (
              <div className="meta" style={{ padding: '8px 0' }}>{t('activityEmpty')}</div>
            ) : (
              <div className="col">
                {order.auditLog.map((e, i) => {
                  const { tag, cls } = auditTag(e);
                  const isError = e.tone === AuditTone.ERROR || e.tone === AuditTone.WARNING;
                  return (
                    <div
                      key={e.id}
                      className="row gap-12"
                      style={{
                        padding: '8px 0',
                        borderBottom:
                          i === order.auditLog.length - 1 ? 'none' : '1px solid var(--line-2)',
                      }}
                    >
                      <div className="mono meta" style={{ width: 110, flexShrink: 0 }}>
                        {auditTime(e.createdAt)}
                      </div>
                      <div style={{ width: 80, flexShrink: 0 }}>
                        <span className={`pill ${cls}`}>{t(tag === 'staff' ? 'actorStaff' : 'actorSystem')}</span>
                      </div>
                      <div className="meta" style={{ width: 100, flexShrink: 0 }}>
                        {e.actorName ?? (e.actorType === AuditActorType.USER ? t('actorStaff') : t('actorSystem'))}
                      </div>
                      <div style={{ fontSize: 14, color: isError ? 'var(--red)' : 'var(--ink)' }}>
                        {e.message}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right rail */}
        <div
          style={{
            width: 340,
            background: '#fff',
            borderLeft: '1px solid var(--line)',
            overflow: 'auto',
            flexShrink: 0,
          }}
        >
          {/* Quick actions (e-mail client / resend proforma / internal note) are not
              built yet — the rail shows only actions that actually work. */}

          {/* Manual status override */}
          <div className="col" style={{ padding: '18px 18px' }}>
            <div className="row between center mb-8">
              <div className="h3" style={{ color: 'var(--red)' }}>{t('overrideHeading')}</div>
              <div className="meta">{t('overrideReconfirmsHint')}</div>
            </div>
            <div className="meta mb-12">
              {t('overrideDescription')}
            </div>

            <div className="col gap-4">
              {STATUSES.map((s) => {
                // Before the admin picks a target, highlight the order's current status.
                const selected = (target ?? order.status) === s.key;
                const current = order.status === s.key;
                return (
                  <button
                    type="button"
                    key={s.key}
                    onClick={() => canOverride && setTarget(s.key)}
                    disabled={!canOverride}
                    className="row between center"
                    style={{
                      padding: '8px 10px',
                      borderRadius: 4,
                      width: '100%',
                      textAlign: 'left',
                      cursor: canOverride ? 'pointer' : 'default',
                      font: 'inherit',
                      color: 'inherit',
                      background: selected ? 'var(--red-bg)' : 'transparent',
                      border: selected ? '1px solid var(--red)' : '1px solid transparent',
                    }}
                  >
                    <span className="row center gap-8">
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '1.5px solid var(--line)',
                          background: selected ? 'var(--red)' : '#fff',
                          display: 'inline-block',
                        }}
                      />
                      <span style={{ fontSize: 14, fontWeight: selected ? 600 : 400 }}>
                        {s.label}
                        {current && <span className="meta" style={{ marginLeft: 6 }}>{t('currentLabel')}</span>}
                      </span>
                    </span>
                    <span className="mono meta" style={{ fontSize: 12 }}>{s.key}</span>
                  </button>
                );
              })}
            </div>

            {targetOk && effectKeys.length > 0 && (
              <div
                className="panel mt-12"
                style={{ padding: '10px 12px', background: 'var(--red-bg)', borderColor: 'var(--red-line)' }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)' }}>
                  {t('overrideWillTrigger', { label: STATUSES.find((s) => s.key === target)?.label ?? '' })}
                </div>
                <div className="col mt-4" style={{ gap: 3 }}>
                  {effectKeys.map((k) => (
                    <div key={k} style={{ fontSize: 13 }}>→ {t(k)}</div>
                  ))}
                </div>
              </div>
            )}

            {targetOk && effectKeys.length === 0 && (
              <div
                className="panel mt-12"
                style={{ padding: '10px 12px', background: 'var(--red-bg)', borderColor: 'var(--red-line)' }}
              >
                <div style={{ fontSize: 13, color: 'var(--red)' }}>
                  {t('overrideNoSideEffects')}
                </div>
              </div>
            )}

            <div className="mt-12">
              <div className="label mb-4">{t('overrideReasonLabel')}</div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('overrideReasonPlaceholder')}
                disabled={!canOverride}
                rows={3}
                className="input"
                style={{
                  width: '100%',
                  minHeight: 60,
                  alignItems: 'flex-start',
                  resize: 'vertical',
                  font: 'inherit',
                }}
              />
            </div>

            {override.error && (
              <div className="mt-12">
                <ErrorState message={override.error.message} />
              </div>
            )}

            {!canOverride && (
              <div className="meta mt-12">
                {t('overrideRestrictedHint')}
              </div>
            )}

            <div className="row gap-8 mt-12">
              <button
                type="button"
                className="btn grow"
                style={{ justifyContent: 'center' }}
                onClick={resetOverride}
                disabled={override.loading}
              >
                {t('btnCancel')}
              </button>
              <button
                type="button"
                className="btn danger grow"
                style={{ justifyContent: 'center' }}
                onClick={() => void confirmOverride()}
                disabled={!canConfirm}
              >
                {override.loading ? t('btnConfirming') : t('btnConfirmOverride')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Topbar-less shell for the loading / error / empty states. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="cl col grow" style={{ background: 'var(--paper)', overflow: 'auto', minWidth: 0 }}>
      <div style={{ padding: '18px 22px' }}>{children}</div>
    </div>
  );
}

/** Labelled key/value row in the Customer panel. */
function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="row between">
      <span className="label">{k}</span>
      <span style={{ fontSize: 14, textAlign: 'right' }}>{v}</span>
    </div>
  );
}
